import { useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'query-string';

import { useAuth, usePost } from 'hooks';
import { format } from 'utils/date';
import { chatOpen, chatClose } from 'services/api';
import { licenseTypeToStringMap } from 'models/licenseType';
import { getNeedPassCaptcha } from 'selectors/rateLimits';

const faqCategories = ['limits'];
const useFreshChat = () => {
  const [chatIsLoaded, setChatIsLoaded] = useState(false);
  const { user, isAuthed } = useAuth();
  const needPassCaptcha = useSelector(getNeedPassCaptcha);
  const { replace, pathname } = useHistory();
  const { search } = useLocation();
  const { faq } = qs.parse(search);
  const [faqCategory, setFaqCategory] = useState(null);

  const [, postChatOpen] = usePost({ url: chatOpen });
  const [, postChatClose] = usePost({ url: chatClose });

  const setChatProps = useCallback(() => {
    const { fcWidget, productFruits, productFruitsIsReady } = window;

    if (productFruits && !!productFruitsIsReady) {
      productFruits.api.inAppCenter.show();
    }

    if (!fcWidget) return;
    fcWidget.setConfig({
      hideFAQ: false,
      headerProperty: {
        hideChatButton: true,
      },
    });
    fcWidget.setExternalId(user.userId);
    fcWidget.user.setProperties({
      firstName: user.userName
        ? user.userName.split(' ')[0]
        : user.displayName.split(' ')[0],
      email: user.userEmail || user.email,
      organisation: user.userCompanyName || user.companyName,
      lastLoggedOn: format(user.userLastLogin, 'DD/MM/YYYY'),
      userId: user.userId,
      accountId: user.accountId,
      isAccountManager: user.isAccountManager,
      isUserManager: user.isUserManager,
      canPushToSalesforce: user.canPushToSalesforce,
      canPushToHubspot: user.canPushToHubspot,
      canPushToDynamics: user.canPushToDynamics,
      licenses: user.licenses
        .map(
          x =>
            `${x.demoMode ? 'Trial' : 'Full'} ${
              licenseTypeToStringMap[x.type]
            } expiry date: ${format(x.expiration, 'DD/MM/YYYY')}`
        )
        .join(', '),
    });

    fcWidget.on('widget:opened', () => postChatOpen());
    fcWidget.on('widget:closed', () => postChatClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, postChatOpen, postChatClose]);

  useEffect(() => {
    const config = {
      token: 'af8aaf7f-94ab-4a14-8a5d-8130b13c9765',
      host: 'https://wchat.freshchat.com',
    };

    const { fcWidget, productFruits, productFruitsIsReady } = window;

    if (!fcWidget) return;

    // Function to set widget configuration
    const setWidgetConfig = (hideFAQ, hideChatButton) => {
      fcWidget.setConfig({
        hideFAQ,
        headerProperty: {
          hideChatButton,
        },
      });
    };

    // Initialize Freshchat widget if not initialized
    const initializeWidget = () => {
      fcWidget.init(config);
      setWidgetConfig(true, false);
      hideProductFruits();
    };

    // Hide ProductFruits if necessary
    const hideProductFruits = () => {
      if (productFruits && !!productFruitsIsReady) {
        productFruits.services?.destroy();
      }
    };

    // Widget initialization logic
    if (!fcWidget.isInitialized()) {
      initializeWidget();
    }

    // User-specific logic
    if (!user) {
      setWidgetConfig(true, false);
      hideProductFruits();
      return;
    }

    // Set chat properties when user is present
    setChatProps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, setChatProps, user?.userId]);

  useEffect(() => {
    const { fcWidget } = window;

    if (!fcWidget) return;

    fcWidget.on('widget:loaded', () => setChatIsLoaded(true));
  }, []);

  useEffect(() => {
    if (chatIsLoaded) {
      const { fcWidget } = window;
      // enter code form
      if (isAuthed && user.tfaEnabled && user.totpVerified === false) {
        fcWidget.setFaqTags({ tags: ['2fa'], filterType: 'category' });
        setChatProps();
        return;
      }

      if (needPassCaptcha) {
        fcWidget.setFaqTags({ tags: ['limits'], filterType: 'category' });
        return;
      }

      if (isAuthed && faqCategory) {
        fcWidget.setFaqTags({ tags: [faqCategory], filterType: 'category' });
        if (!fcWidget?.isOpen()) {
          fcWidget?.open();
        }

        return;
      }

      if (user && user.userEmail) {
        fcWidget.setFaqTags({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chatIsLoaded,
    isAuthed,
    needPassCaptcha,
    setChatProps,
    user?.userId,
    faqCategory,
  ]);

  useEffect(() => {
    if (isAuthed && faqCategories.includes(faq)) {
      setFaqCategory(faq);
      replace(pathname);
      return;
    }
  }, [faq, isAuthed, pathname, replace]);
};

export default useFreshChat;
